import axios from "axios";
import { isRemoteFile } from "components/GeneralFormView/FormField/types";
import SnackBarComponent from "components/SnackBarComponent/SnackBarComponent";
import Connections from "connections";
import { translate } from "helperFunctions/translation";
import { uploadSingleFile } from "helperFunctions/uploadService";
import { observable } from "mobx";
import { any } from "prop-types";

export enum RequestStatus {
  IDLE = "idle",
  LOADING = "loading",
  SUCCESS = "success",
  FAILED = "failed",
}

export interface Match {
  id: number;
  homeTeam: string;
  awayTeam: string;
  matchDatetime: string;
  homeTeamLogo?: string;
  awayTeamLogo?: string;
  homeGoals?: number | null;
  awayGoals?: number | null;
  homePenaltyGoals?: number | null;
  awayPenaltyGoals?: number | null;
}

export interface Prediction {
  predictionId: number;
  matchId: number;
  userId: number;
  homeGoals: number;
  awayGoals: number;
  homePenaltyGoals?: number | null;
  awayPenaltyGoals?: number | null;
}

export interface PredictionView {
  prediction_id: number;
  match_id: number;
  user_id: number;
  predHomeGoals: number | null;
  predAwayGoals: number | null;
  predHomePenaltyGoals: number | null;
  predAwayPenaltyGoals: number | null;
  homeTeam: string;
  awayTeam: string;
  matchDatetime: string;
  homeGoals: number | null;
  awayGoals: number | null;
  homePenaltyGoals: number | null;
  awayPenaltyGoals: number | null;
  homeTeamLogo: string;
  awayTeamLogo: string;
  membershipId: string;
  email: string;
  isCorrect: number | null;
  isIncorrect: number | null;
}

export class AfconStore {
  @observable matches: Match[] = [];
  @observable predictionsByMatch: Record<number, PredictionView[]> = {};

  @observable matchesLoadState: RequestStatus = RequestStatus.IDLE;
  @observable predictionsLoadState: RequestStatus = RequestStatus.IDLE;

  setMatches(matches: Match[]) {
    this.matches = matches;
  }

  setPredictions(matchId: number, predictions: PredictionView[]) {
    this.predictionsByMatch[matchId] = predictions;
  }

  setMatchesLoadState(state: RequestStatus) {
    this.matchesLoadState = state;
  }

  setPredictionsLoadState(state: RequestStatus) {
    this.predictionsLoadState = state;
  }

  // ---------------- MATCHES ----------------
  async fetchMatches() {
    try {
      this.setMatchesLoadState(RequestStatus.LOADING);
      const { data } = await Connections.wadiDeglaAxios.httpGet(
        "/afcon/matches"
      );
      this.setMatches(data);
      this.setMatchesLoadState(RequestStatus.SUCCESS);
    } catch (err) {
      this.handleError(err);
      this.setMatchesLoadState(RequestStatus.FAILED);
    }
  }

  async createMatch(match: Omit<Match, "matchId">) {
    try {
      this.setMatchesLoadState(RequestStatus.LOADING);
      if (
        match.homeTeamLogo &&
        typeof match.homeTeamLogo !== "string" &&
        !isRemoteFile(match.homeTeamLogo)
      ) {
        match.homeTeamLogo = await uploadSingleFile(
          match.homeTeamLogo as any as File
        );
      }
      if (
        match.awayTeamLogo &&
        typeof match.awayTeamLogo !== "string" &&
        !isRemoteFile(match.awayTeamLogo)
      ) {
        match.awayTeamLogo = await uploadSingleFile(
          match.awayTeamLogo as any as File
        );
      }
      const { data } = await Connections.wadiDeglaAxios.httpPost(
        "/afcon/matches",
        match
      );
      this.matches.push(data);
      this.setMatchesLoadState(RequestStatus.SUCCESS);
    } catch (err) {
      this.handleError(err);
      this.setMatchesLoadState(RequestStatus.FAILED);
    }
  }

  async updateMatch(matchId: number, match: Omit<Match, "matchId">) {
    try {
      this.setMatchesLoadState(RequestStatus.LOADING);
      if (
        match.homeTeamLogo &&
        typeof match.homeTeamLogo !== "string" &&
        !isRemoteFile(match.homeTeamLogo)
      ) {
        match.homeTeamLogo = await uploadSingleFile(
          match.homeTeamLogo as any as File
        );
      }
      if (
        match.awayTeamLogo &&
        typeof match.awayTeamLogo !== "string" &&
        !isRemoteFile(match.awayTeamLogo)
      ) {
        match.awayTeamLogo = await uploadSingleFile(
          match.awayTeamLogo as any as File
        );
      }
      const { data } = await Connections.wadiDeglaAxios.httpPut(
        `/afcon/matches/${matchId}`,
        match
      );
      const index = this.matches.findIndex((m) => m.id === matchId);
      if (index !== -1) this.matches[index] = data;
      this.setMatchesLoadState(RequestStatus.SUCCESS);
    } catch (err) {
      this.handleError(err);
      this.setMatchesLoadState(RequestStatus.FAILED);
    }
  }

  async deleteMatch(matchId: number) {
    try {
      this.setMatchesLoadState(RequestStatus.LOADING);
      await Connections.wadiDeglaAxios.httpDelete(`/afcon/matches/${matchId}`);
      this.matches = this.matches.filter((m) => m.id !== matchId);
      this.setMatchesLoadState(RequestStatus.SUCCESS);
    } catch (err) {
      this.handleError(err);
      this.setMatchesLoadState(RequestStatus.FAILED);
    }
  }

  // ---------------- PREDICTIONS ----------------
  async fetchPredictionsByMatch(matchId: number) {
    try {
      this.setPredictionsLoadState(RequestStatus.LOADING);
      const { data } = await Connections.wadiDeglaAxios.httpGet(
        `/afcon/matches/${matchId}/predictions`
      );
      this.setPredictions(matchId, data);
      this.setPredictionsLoadState(RequestStatus.SUCCESS);
    } catch (err) {
      this.handleError(err);
      this.setPredictionsLoadState(RequestStatus.FAILED);
    }
  }

  async downloadPredictionsXlsx(matchId: number) {
    try {
      this.setPredictionsLoadState(RequestStatus.LOADING);

      const response = await Connections.wadiDeglaAxios.httpGet(
        `/afcon/matches/${matchId}/predictions`,
        {
          headers: {
            Accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
          responseType: "blob", // REQUIRED for XLSX
        }
      );

      // Create file name
      const fileName = `match-${matchId}-predictions.xlsx`;

      // Create blob URL
      const url = window.URL.createObjectURL(new Blob([response]));

      // Create link and auto-trigger download
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      this.setPredictionsLoadState(RequestStatus.SUCCESS);
    } catch (err) {
      console.error(err);
      this.handleError(err);
      this.setPredictionsLoadState(RequestStatus.FAILED);
    }
  }

  private handleError(err: any) {
    SnackBarComponent.getInstance().openSnackbar(
      (err as any).response?.data?.message || translate("errorMessage"),
      8000,
      "error"
    );
  }
}
